/**
 * The browser's discussion renderer for one feedback request.
 *
 * This is the thing that stops a browser-only recipient hitting a dead end. A
 * desktop recipient who cannot answer the question as asked can go argue in a
 * DM; in the browser this thread is the only channel, which is why it ships
 * with the respond surface rather than after it.
 *
 * It is deliberately not the desktop `CommentThread`. That component is bound
 * to a mention directory, workspace-scoped resource previews and
 * `collab-asset://` attachments, none of which exist here — so the respond
 * card takes an opaque `discussion` node and each host fills it with what it
 * can actually honor. What the browser can honor is: read the thread, write
 * plain text, and never lose what someone typed.
 *
 * The write path is optimistic with a visible failure, matching the desktop
 * pattern exactly on the one detail that matters: a failed comment keeps its
 * `clientMutationId`, so pressing Retry dedupes server-side instead of posting
 * the same sentence twice.
 */

import { useCallback, useMemo, useState } from 'react';
import type {
  Actor,
  FeedbackDiscussionComment,
  FeedbackRequestRecipient,
  RichCommentBody,
} from '@nimbalyst/collab-protocol';

/** Appends one comment and resolves with the comment the server stored. */
export type FeedbackDiscussionPost = (
  clientMutationId: string,
  body: RichCommentBody,
) => Promise<FeedbackDiscussionComment>;

export interface FeedbackRequestDiscussionProps {
  /** The server's thread, straight off the request projection. */
  comments: readonly FeedbackDiscussionComment[];
  /** The reader's org-scoped member id; their own rows are signed "You". */
  viewerUserId: string;
  /**
   * The only name directory the browser has. A feedback request carries
   * resolved names for its recipients, so no roster fetch is needed to sign
   * the rows that realistically exist on this thread.
   */
  recipients: readonly FeedbackRequestRecipient[];
  /**
   * Absent when the request is terminal or the connection is not up yet. The
   * server refuses the write in both cases, so no composer is offered — an
   * affordance guaranteed to fail is worse than none.
   */
  post?: FeedbackDiscussionPost;
  /** Stated in place of the composer when there is a reason worth giving. */
  readOnlyReason?: string;
  createMutationId?: () => string;
}

/** A comment this tab authored that the server has not yet acknowledged. */
interface PendingComment {
  clientMutationId: string;
  body: RichCommentBody;
  status: 'sending' | 'failed';
  error?: string;
}

function defaultMutationId(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `feedback-comment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function whenPosted(createdAt: number): string {
  return new Date(createdAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * A refused write, in the reader's terms.
 *
 * The discussion budget is the one that matters here: it is a real 400 from
 * the room, it is not the reader's fault, and retrying will not clear it — so
 * it must not read as a transient "could not send".
 */
export function describeCommentFailure(error: unknown): string {
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : '';
  if (code === 'feedbackDiscussionLimitExceeded') {
    return 'This discussion is full. It has reached the size this request can hold, '
      + 'so no further comments can be added to it.';
  }
  if (code === 'requestNotOpen') {
    return 'This request stopped taking comments before this one was sent.';
  }
  if (code === 'invalidComment') {
    return 'That comment was rejected as malformed.';
  }
  return error instanceof Error && error.message
    ? error.message
    : 'Your comment could not be sent.';
}

function actorLabel(
  actor: Actor,
  viewerUserId: string,
  names: ReadonlyMap<string, string>,
): string {
  if (actor.kind === 'agent') return actor.sessionName || 'Agent';
  const userId = actor.onBehalfOfUserId;
  if (userId && userId === viewerUserId) return 'You';
  // Falling back to the raw member id is deliberate: it is ugly, but it is
  // distinct per person and it never attributes a comment to the wrong one.
  return names.get(userId) ?? userId ?? 'Someone';
}

export function FeedbackRequestDiscussion({
  comments,
  viewerUserId,
  recipients,
  post,
  readOnlyReason,
  createMutationId = defaultMutationId,
}: FeedbackRequestDiscussionProps) {
  const [pending, setPending] = useState<PendingComment[]>([]);
  /**
   * Comments the server acknowledged to this tab. They are held until the
   * following snapshot carries them, so the sentence someone just sent does
   * not blink out of the thread while the round trip finishes.
   */
  const [acknowledged, setAcknowledged] = useState<FeedbackDiscussionComment[]>([]);
  const [draft, setDraft] = useState('');

  const names = useMemo(
    () => new Map(recipients.map((recipient) => [recipient.userId, recipient.name])),
    [recipients],
  );

  const rows = useMemo(() => {
    const byId = new Map<string, FeedbackDiscussionComment>();
    for (const comment of [...acknowledged, ...comments]) byId.set(comment.id, comment);
    return [...byId.values()].sort((left, right) =>
      left.createdAt - right.createdAt || left.id.localeCompare(right.id));
  }, [acknowledged, comments]);

  const send = useCallback(async (entry: PendingComment, sendComment: FeedbackDiscussionPost) => {
    setPending((current) => current.map((candidate) =>
      candidate.clientMutationId === entry.clientMutationId
        ? { ...candidate, status: 'sending', error: undefined }
        : candidate));
    try {
      const created = await sendComment(entry.clientMutationId, entry.body);
      setAcknowledged((current) => [...current, created]);
      setPending((current) => current.filter(
        (candidate) => candidate.clientMutationId !== entry.clientMutationId,
      ));
    } catch (error) {
      setPending((current) => current.map((candidate) =>
        candidate.clientMutationId === entry.clientMutationId
          ? { ...candidate, status: 'failed', error: describeCommentFailure(error) }
          : candidate));
    }
  }, []);

  const submit = useCallback(() => {
    const text = draft.trim();
    if (!text || !post) return;
    const entry: PendingComment = {
      // Minted once, here. Retry reuses it rather than minting a new one, so a
      // send that actually reached the room and failed on the way back dedupes
      // instead of posting twice.
      clientMutationId: createMutationId(),
      body: { version: 1, format: 'plainText', text },
      status: 'sending',
    };
    setPending((current) => [...current, entry]);
    setDraft('');
    void send(entry, post);
  }, [createMutationId, draft, post, send]);

  return (
    <div className="feedback-request-discussion flex flex-col gap-2.5">
      {rows.length === 0 && pending.length === 0 && (
        <p className="m-0 text-xs text-nim-muted">
          No comments yet.
          {post ? ' Say what the question is missing, or what you would need to answer it.' : ''}
        </p>
      )}

      {(rows.length > 0 || pending.length > 0) && (
        <ol className="feedback-discussion-list m-0 flex list-none flex-col gap-2.5 p-0">
          {rows.map((comment) => (
            <li
              key={comment.id}
              className="feedback-discussion-comment flex flex-col gap-0.5"
              data-testid="feedback-discussion-comment"
            >
              <div className="flex flex-wrap items-baseline gap-2 text-[0.6875rem] text-nim-faint">
                <span className="font-semibold text-nim-muted">
                  {actorLabel(comment.actor, viewerUserId, names)}
                </span>
                <time dateTime={new Date(comment.createdAt).toISOString()}>
                  {whenPosted(comment.createdAt)}
                </time>
              </div>
              <p className="m-0 whitespace-pre-wrap break-words text-xs leading-relaxed text-nim select-text">
                {comment.body.text}
              </p>
            </li>
          ))}

          {pending.map((entry) => (
            <li
              key={entry.clientMutationId}
              className="feedback-discussion-comment flex flex-col gap-0.5"
              data-testid="feedback-discussion-comment"
              data-comment-status={entry.status}
            >
              <div className="flex flex-wrap items-baseline gap-2 text-[0.6875rem] text-nim-faint">
                <span className="font-semibold text-nim-muted">You</span>
                {entry.status === 'sending' && <span>Sending…</span>}
              </div>
              <p
                className={`m-0 whitespace-pre-wrap break-words text-xs leading-relaxed select-text ${
                  entry.status === 'failed' ? 'text-nim-muted' : 'text-nim'
                }`}
              >
                {entry.body.text}
              </p>
              {entry.status === 'failed' && (
                <div
                  className="feedback-discussion-failed flex flex-wrap items-baseline gap-2 text-[0.6875rem] text-nim-error"
                  data-testid="feedback-discussion-failed"
                >
                  <span className="font-semibold">Not sent.</span>
                  <span>{entry.error}</span>
                  {post && (
                    <button
                      type="button"
                      className="underline"
                      data-testid="feedback-discussion-retry"
                      onClick={() => void send(entry, post)}
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {post
        ? (
          <form
            className="feedback-discussion-composer flex flex-col items-end gap-1.5"
            data-testid="feedback-discussion-composer"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <textarea
              className="w-full resize-y rounded border border-nim bg-nim px-2 py-1.5 text-xs leading-relaxed text-nim placeholder:text-nim-faint focus:border-nim-focus focus:outline-none"
              rows={2}
              value={draft}
              aria-label="Add a comment"
              placeholder="Add a comment"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends, Shift+Enter breaks the line: the same contract
                // as every other composer in the product.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  submit();
                }
              }}
            />
            <button
              type="submit"
              className="rounded bg-nim-primary px-2.5 py-1 text-xs font-medium text-[var(--nim-on-primary)] disabled:opacity-50"
              data-testid="feedback-discussion-send"
              disabled={draft.trim().length === 0}
            >
              Comment
            </button>
          </form>
        )
        : readOnlyReason
          ? (
            <p
              className="feedback-discussion-read-only m-0 text-xs text-nim-muted"
              data-testid="feedback-discussion-read-only"
            >
              {readOnlyReason}
            </p>
          )
          : null}
    </div>
  );
}
